export function createFukushizaiProjectionContextService(deps) {
    const {
        entrydataRef,
        selectedFukushizaiContextRef,
        selectedQualityContextRef,
        fukushizaiProjectionGuardRef,
        fukushizaiProjectionGuardTimerRef,
        QUALITY_ROOT_DATA_PREFIXES,
        isEmpty,
        DicVal,
        normalizeFukushizaiContext,
        createEmptyFukushizaiContext,
        getFukushizaiAllFieldIds,
        clearQualityBaseMergedFields,
        logEntryVisualTrace
    } = deps;

    // entrydata に投影済みの副資材/品質表示関連フィールドを掃除します。
    function clearFukushizaiMergedFields(entryObj) {
        if (isEmpty(entryObj) || typeof entryObj !== "object") return;

        const deletedKeys = [];
        const rememberDeletedKey = (key) => {
            if (deletedKeys.length >= 12) return;
            if (deletedKeys.indexOf(key) !== -1) return;
            deletedKeys.push(key);
        };
        const beforeKeyCount = Object.keys(entryObj).length;

        Object.keys(entryObj).forEach((key) => {
            const cid = String(key ?? "").trim().toUpperCase();
            const lower = String(key ?? "").trim().toLowerCase();

            // option キャッシュは UI 再利用用なので保持します。
            if (lower === "__fukushizaiitemoptions" || lower === "__fukushizaicoloroptions") return;

            // 投影済みフィールドと source 情報をまとめて除去します。
            if (cid.startsWith("SSDZ_")
                || QUALITY_ROOT_DATA_PREFIXES.some((prefix) => cid.startsWith(prefix))
                || cid.startsWith("__FUKUSHIZAI")
                || cid.startsWith("__KOMONOFUDA")
                || cid.startsWith("__SOURCE")) {
                rememberDeletedKey(key);
                delete entryObj[key];
            }
        });

        getFukushizaiAllFieldIds().forEach((fieldId) => {
            [fieldId, `${fieldId}_nm`, `${fieldId}_NM`, `${fieldId}_cd`, `${fieldId}_CD`].forEach((key) => {
                if (!Object.prototype.hasOwnProperty.call(entryObj, key)) return;
                rememberDeletedKey(key);
                delete entryObj[key];
            });
        });

        clearQualityBaseMergedFields(entryObj);
        delete entryObj.__FUKUSHIZAI_PROJECTED_ITEMCD;
        delete entryObj.__FUKUSHIZAI_PROJECTED_GROUPNO;
        delete entryObj.__FUKUSHIZAI_PROJECTED_COLORCD;

        logEntryVisualTrace("clearFukushizaiMergedFields", {
            beforeKeyCount,
            afterKeyCount: Object.keys(entryObj).length,
            deletedKeyCount: deletedKeys.length,
            deletedKeySample: deletedKeys
        });
    }

    // entrydata に現在の副資材投影 context を保持します。
    function setFukushizaiEntryProjectionContext(entryObj, context) {
        if (isEmpty(entryObj) || typeof entryObj !== "object") return;
        const normalized = normalizeFukushizaiContext(context);
        entryObj.__FUKUSHIZAI_PROJECTED_ITEMCD = normalized.itemCd;
        entryObj.__FUKUSHIZAI_PROJECTED_GROUPNO = normalized.groupNo;
        entryObj.__FUKUSHIZAI_PROJECTED_COLORCD = normalized.colorCd;
    }

    // entrydata から現在の副資材投影 context を復元します。
    function getFukushizaiEntryProjectionContext(entryObj) {
        if (isEmpty(entryObj) || typeof entryObj !== "object") return createEmptyFukushizaiContext();
        return normalizeFukushizaiContext({
            itemCd: DicVal(entryObj, "__FUKUSHIZAI_PROJECTED_ITEMCD", ""),
            groupNo: DicVal(entryObj, "__FUKUSHIZAI_PROJECTED_GROUPNO", ""),
            colorCd: DicVal(entryObj, "__FUKUSHIZAI_PROJECTED_COLORCD", "")
        });
    }

    // BASE 編集対象の context は entrydata 側の投影値を優先して解決します。
    function resolveFukushizaiBaseEditContext() {
        const projected = getFukushizaiEntryProjectionContext(entrydataRef.current);
        if (projected.itemCd !== "" && projected.groupNo !== "") return projected;
        return normalizeFukushizaiContext(selectedFukushizaiContextRef.current);
    }

    // ---------------------------------------------------------------------
    // 品質表示側の投影 context(副資材とは別マーカーで保持する)。
    // 副資材と共用すると投影の後勝ちで context が混線し、
    // BASE 編集が別ドメインの item/group へ書き込まれる。
    // ---------------------------------------------------------------------
    function setQualityEntryProjectionContext(entryObj, context) {
        if (isEmpty(entryObj) || typeof entryObj !== "object") return;
        const normalized = normalizeFukushizaiContext(context);
        entryObj.__QUALITY_PROJECTED_ITEMCD = normalized.itemCd;
        entryObj.__QUALITY_PROJECTED_GROUPNO = normalized.groupNo;
        entryObj.__QUALITY_PROJECTED_COLORCD = normalized.colorCd;
    }

    function getQualityEntryProjectionContext(entryObj) {
        if (isEmpty(entryObj) || typeof entryObj !== "object") return createEmptyFukushizaiContext();
        return normalizeFukushizaiContext({
            itemCd: DicVal(entryObj, "__QUALITY_PROJECTED_ITEMCD", ""),
            groupNo: DicVal(entryObj, "__QUALITY_PROJECTED_GROUPNO", ""),
            colorCd: DicVal(entryObj, "__QUALITY_PROJECTED_COLORCD", "")
        });
    }

    // 品質表示BASE編集対象の context を品質側の投影値を優先して解決します。
    function resolveQualityBaseEditContext() {
        const projected = getQualityEntryProjectionContext(entrydataRef.current);
        if (projected.itemCd !== "" && projected.groupNo !== "") return projected;
        return normalizeFukushizaiContext(selectedQualityContextRef.current);
    }

    // 表示 row にだけ付く投影補助フィールドを除去します。
    function clearFukushizaiDisplayProjectionFields(row) {
        if (isEmpty(row) || typeof row !== "object") return row;
        getFukushizaiAllFieldIds().forEach((fieldId) => {
            delete row[`${fieldId}_nm`];
            delete row[`${fieldId}_NM`];
            delete row[`${fieldId}_cd`];
            delete row[`${fieldId}_CD`];
        });
        return row;
    }

    // 投影直後の循環同期を避けるため 1tick ガードを有効化します。
    function activateFukushizaiProjectionGuard() {
        fukushizaiProjectionGuardRef.current = true;
        if (!isEmpty(fukushizaiProjectionGuardTimerRef.current)) {
            clearTimeout(fukushizaiProjectionGuardTimerRef.current);
        }
        fukushizaiProjectionGuardTimerRef.current = setTimeout(() => {
            fukushizaiProjectionGuardRef.current = false;
            fukushizaiProjectionGuardTimerRef.current = null;
        }, 0);
    }

    return {
        clearFukushizaiMergedFields,
        setFukushizaiEntryProjectionContext,
        getFukushizaiEntryProjectionContext,
        resolveFukushizaiBaseEditContext,
        setQualityEntryProjectionContext,
        getQualityEntryProjectionContext,
        resolveQualityBaseEditContext,
        clearFukushizaiDisplayProjectionFields,
        activateFukushizaiProjectionGuard
    };
}
